import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import NotificationsIcon from "@mui/icons-material/Notifications";
import BusinessIcon from "@mui/icons-material/Business";
import TuneIcon from "@mui/icons-material/Tune";
import ProspectGenerator from "./ProspectGenerator";

const BRAND_BLUE = "#00336C";
const BRAND_GREEN = "#648315";
const PAGE_BG = "#f4f7fc";

const CARDS = [
  {
    icon: <TuneIcon sx={{ fontSize: 18 }} />,
    title: "Set Your Criteria",
    copy: "Filter by sector, region, growth signals, and company size to narrow your search before generating.",
    step: "1",
  },
  {
    icon: <AutoAwesomeIcon sx={{ fontSize: 18 }} />,
    title: "Generate a List",
    copy: "Hit Generate to get up to 20 AI-matched companies. Results update each time based on your filters.",
    step: "2",
  },
  {
    icon: <NotificationsIcon sx={{ fontSize: 18 }} />,
    title: "Review Weekly Lists",
    copy: "Schedule automated reports and review this week's saved results alongside any manually generated lists.",
    step: "3",
  },
  {
    icon: <BusinessIcon sx={{ fontSize: 18 }} />,
    title: "Add to Pipeline",
    copy: "Add strong matches directly to Prospect Companies to begin tracking and outreach.",
    step: "4",
  },
];

function HowItWorksCard({ icon, title, copy, step }) {
  return (
    <Box
      sx={{
        border: "1px solid #dce4ef",
        borderRadius: 2,
        backgroundColor: "#ffffff",
        p: 2,
        display: "flex",
        gap: 1.5,
        alignItems: "flex-start",
      }}
    >
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: 1.5,
          backgroundColor: "rgba(100, 131, 21, 0.09)",
          color: BRAND_GREEN,
          display: "grid",
          placeItems: "center",
          flex: "0 0 auto",
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.4 }}>
          <Typography
            sx={{
              color: "#9bafc4",
              fontSize: 11,
              fontWeight: 800,
              textTransform: "uppercase",
              letterSpacing: 0.6,
            }}
          >
            Step {step}
          </Typography>
        </Stack>
        <Typography sx={{ color: BRAND_BLUE, fontWeight: 700, fontSize: 13.5, lineHeight: 1.3, mb: 0.4 }}>
          {title}
        </Typography>
        <Typography sx={{ color: "#6b85a1", fontSize: 12.5, lineHeight: 1.55 }}>
          {copy}
        </Typography>
      </Box>
    </Box>
  );
}

export default function AiProspectingPage() {
  return (
    <Box sx={{ width: "100%", minHeight: "100vh", backgroundColor: PAGE_BG, pb: 5 }}>
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 5,
          borderBottom: "1px solid #dce4ef",
          background: "#ffffff",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
            px: { xs: 2, md: 3 },
            py: 1.75,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                background: `linear-gradient(135deg, ${BRAND_BLUE} 0%, #1a5294 100%)`,
                color: "#ffffff",
                display: "grid",
                placeItems: "center",
                flex: "0 0 auto",
                boxShadow: "0 4px 14px rgba(0, 51, 108, 0.28)",
              }}
            >
              <AutoAwesomeIcon sx={{ fontSize: 21 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  color: BRAND_BLUE,
                  fontSize: 21,
                  fontWeight: 800,
                  lineHeight: 1.1,
                  letterSpacing: 0,
                }}
              >
                AI Prospecting
              </Typography>
              <Typography sx={{ color: "#6b85a1", fontSize: 13, mt: 0.2 }}>
                Generate targeted company lists from your criteria
              </Typography>
            </Box>
          </Box>

        </Box>
      </Box>

      <Box sx={{ px: { xs: 2, md: 3 }, pt: 3 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
            gap: 1.5,
            mb: 2.5,
          }}
        >
          {CARDS.map((card) => (
            <HowItWorksCard key={card.step} {...card} />
          ))}
        </Box>

        <ProspectGenerator />
      </Box>
    </Box>
  );
}
